import { StatsigClient } from "@statsig/js-client";
import type { Experiment, StatsigUser } from "@statsig/js-client";
import logger from "./logger/logger";

// Derived from RETURNS_APP_CLIENT_STATSIG_SECRET secret.
const getStatsigClientKey = (): string => window?.STATSIG_CLIENT_KEY ?? "";

export interface IFeatureFlag {
    isStatsigInitialized: boolean,
    initialize(user: StatsigClientUser),
    isEnabled(gate: string): boolean,
    getExperiment(name: string): Experiment | null,
    getExperimentData(experiment: Experiment | null): StatsigExperimentData,
    logEvent(eventName: string, value: string | null, custom?: Record<string, string> | null | undefined),
}

export enum statsigFeatureFlag {
    remove_multi_bag_prompt_for_single_item = "remove_multi_bag_prompt_for_single_item",
}

export enum statsigExperiment{
    dropoff_methods_claims_and_labels = "dropoff_methods_-_claims_and_labels"
}

export enum statsigExperimentValues{
    dropoff_methods_claims_and_labels_label = "label",
}

export enum statsigEvents{
    selected_dropoff_method = "selected_dropoff_method",
    dropoff_methods_claims_and_labels_experiment = "dropoff_methods_claims_and_labels_experiment"
}

export interface StatsigClientUser {
    retailerID?: string
    environment?: string
    email?: string,
    locale?: string,
}

export interface StatsigExperimentData {
    group?: string,
    blockedBy?: string
  }

class FeatureFlag implements IFeatureFlag {
    isStatsigInitialized = false
    private client: StatsigClient | null = null

    async initialize(user: StatsigClientUser) {
        if (!user) return

        if (user!.environment?.includes("dev") ||
        user!.environment?.includes("test") ||
        user!.environment?.includes("local")) {
            user.environment = "development";
        } else if (user!.environment?.includes("prod") || user!.environment?.includes("production")) {
            user.environment = "production";
        }

        const statsigUser: StatsigUser = {
            userID: user?.email,
            custom: {
              retailerID: user?.retailerID || "",
              locale: user?.locale || "",
            },
            customIDs: {
                retailerID: user?.retailerID || ""
            }
        }

        try {
            if (this.client) {
                await this.client.updateUserAsync(statsigUser);
            } else {
                const clientKey = getStatsigClientKey();
                if (!clientKey) {
                    logger.Error("[StatsigClient] No client key in appConfig, skipping initialization");
                    return;
                }
                this.client = new StatsigClient(
                    clientKey,
                    statsigUser,
                    { environment: { tier: user.environment || "" } }
                );
                await this.client.initializeAsync();
            }
            this.isStatsigInitialized = true;
        } catch (e) {
            logger.Error("[StatsigClient] Error to initialize: " + e)
        }
    }

    isEnabled(gate: string): boolean {
        if (!this.client) {
            return false
        }
        try {
            return this.client.checkGate(gate)
        } catch(e) {
            logger.Error("[StatsigClient] Error to verify feature flag '" + gate + "': " + e)
            return false
        }
    }

    getExperiment(name: string): Experiment | null {
        if (!this.client) return null
        try {
            return this.client.getExperiment(name);
        } catch (e) {
            logger.Error("[StatsigClient] Error to retrieve experiment '" + name + "': " + e)
            return null
        }
    }

    getExperimentData(experimentResult: Experiment | null): StatsigExperimentData {
        if (!experimentResult) return {}
        return {
            group: experimentResult.groupName as string,
            blockedBy: defineExperimentBlockingReason(experimentResult.ruleID, experimentResult.details?.reason ?? ""),
            ...experimentResult.value,
        }
    }

    logEvent(eventName: string, value: string | null, custom?: Record<string, string> | null | undefined) {
        if (eventName == "") {
            logger.Error("[StatsigClient] Log Event error: Event Name cannot be empty.");
            return
        }
        if (!this.client) {
            return
        }
        try {
            this.client.logEvent(eventName, value ?? undefined, custom ?? undefined);
        } catch (e) {
            logger.Error("[StatsigClient] Error to log event: " + e);
        }
    }
}

const defineExperimentBlockingReason = (ruleID: string, evaluationReason: string): string =>  {
    if (ruleID === "targetingGate") return "Target Gate"
    else if (ruleID.includes(":id_override")) return "Override Rule"
    else if (evaluationReason === "Uninitialized") return "Statsig Fail to deliver experiment data"
    else return ""
}

export const statsigClient: IFeatureFlag = new FeatureFlag()
